import {formatChemistryText} from './chemical-typography';
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Clock3, Search } from "lucide-react";
import type { ActivityDefinition, SchoolLevel } from "./catalog";
import "./activity-picker.css";

type UsageStatus = "not-evaluated" | "in-progress" | "evaluated";

type ActivitySessionLike = {
  classId: string;
  activityId: string;
  startedAt: string;
  evaluations?: Record<string, Record<string, unknown>>;
};

type ActivityWithTheme = ActivityDefinition & { theme?: string };

type ActivityPickerProps = {
  disabled?: boolean;
  activities: ActivityDefinition[];
  schoolLevel: SchoolLevel;
  classId: string;
  selectedId: string;
  currentSessionActivityId?: string;
  sessions: ActivitySessionLike[];
  onSelect: (activityId: string) => void;
};

const themeFor = (activity: ActivityDefinition) => (activity as ActivityWithTheme).theme?.trim() ?? "";
const statusLabel: Record<UsageStatus, string> = {
  "not-evaluated": "Non évaluée",
  "in-progress": "En cours",
  evaluated: "Évaluée",
};

function hasEvaluation(session: ActivitySessionLike) {
  return Object.values(session.evaluations ?? {}).some((values) => Object.keys(values ?? {}).length > 0);
}

export function ActivityPicker({ activities, schoolLevel, classId, selectedId, currentSessionActivityId, sessions, onSelect, disabled = false }: ActivityPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [theme, setTheme] = useState("all");
  const [chapter, setChapter] = useState("all");
  const [status, setStatus] = useState<"all" | UsageStatus>("all");

  const levelActivities = useMemo(() => activities
    .filter((activity) => activity.schoolLevel === schoolLevel)
    .slice()
    .sort((a, b) => {
      const themeDelta = themeFor(a).localeCompare(themeFor(b), "fr", { sensitivity: "base" });
      if (themeDelta) return themeDelta;
      const chapterDelta = a.chapter.localeCompare(b.chapter, "fr", { sensitivity: "base" });
      if (chapterDelta) return chapterDelta;
      return a.title.localeCompare(b.title, "fr", { sensitivity: "base" });
    }), [activities, schoolLevel]);

  const selectedActivity = levelActivities.find((activity) => activity.id === selectedId) ?? null;

  const statuses = useMemo(() => {
    const values = new Map<string, UsageStatus>();
    levelActivities.forEach((activity) => values.set(activity.id, "not-evaluated"));
    sessions
      .filter((session) => session.classId === classId && hasEvaluation(session))
      .forEach((session) => {
        if (values.has(session.activityId)) values.set(session.activityId, "evaluated");
      });
    if (currentSessionActivityId && values.has(currentSessionActivityId)) values.set(currentSessionActivityId, "in-progress");
    return values;
  }, [levelActivities, sessions, classId, currentSessionActivityId]);

  const themes = useMemo(() => [...new Set(levelActivities.map(themeFor).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" })), [levelActivities]);

  const chapters = useMemo(() => [...new Set(levelActivities
    .filter((activity) => theme === "all" || themeFor(activity) === theme)
    .map((activity) => activity.chapter)
    .filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" })), [levelActivities, theme]);

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("fr");
    return levelActivities.filter((activity) => {
      const usageStatus = statuses.get(activity.id) ?? "not-evaluated";
      if (theme !== "all" && themeFor(activity) !== theme) return false;
      if (chapter !== "all" && activity.chapter !== chapter) return false;
      if (status !== "all" && usageStatus !== status) return false;
      if (!query) return true;
      return `${formatChemistryText(activity.title)} ${themeFor(activity)} ${activity.chapter}`.toLocaleLowerCase("fr").includes(query);
    });
  }, [levelActivities, statuses, search, theme, chapter, status]);

  const recent = useMemo(() => {
    const byId = new Map(levelActivities.map((activity) => [activity.id, activity]));
    const seen = new Set<string>();
    return sessions
      .filter((session) => session.classId === classId && byId.has(session.activityId))
      .slice()
      .sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))
      .flatMap((session) => {
        if (seen.has(session.activityId)) return [];
        seen.add(session.activityId);
        const activity = byId.get(session.activityId);
        return activity ? [activity] : [];
      })
      .slice(0, 3);
  }, [levelActivities, sessions, classId]);

  const showRecent = !search.trim() && theme === "all" && chapter === "all" && status === "all" && recent.length > 0;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    setTheme("all");
    setChapter("all");
    setStatus("all");
    setSearch("");
  }, [schoolLevel]);

  function choose(activityId: string) {
    onSelect(activityId);
    setOpen(false);
    setSearch("");
  }

  return (
    <div className={`activity-picker ${open ? "open" : ""}`} ref={rootRef}>
      <button className="activity-picker-trigger" type="button" disabled={disabled} onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span>{formatChemistryText(selectedActivity?.title ?? "Aucune activité")}</span>
        <ChevronDown size={16} />
      </button>

      {open && (
        <div className="activity-picker-panel">
          <div className="activity-picker-search">
            <Search size={17} />
            <input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher une activité…" />
          </div>

          <div className="activity-picker-filters">
            <label><span>Thème</span><select value={theme} onChange={(event) => { setTheme(event.target.value); setChapter("all"); }}><option value="all">Tous les thèmes</option>{themes.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
            <label><span>Chapitre</span><select value={chapter} onChange={(event) => setChapter(event.target.value)}><option value="all">Tous les chapitres</option>{chapters.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
            <label><span>Statut</span><select value={status} onChange={(event) => setStatus(event.target.value as "all" | UsageStatus)}><option value="all">Tous</option><option value="not-evaluated">Non évaluée</option><option value="in-progress">En cours</option><option value="evaluated">Évaluée</option></select></label>
          </div>

          <button type="button" className="activity-picker-none" onClick={() => choose("")}>Aucune activité <small>Participation uniquement</small></button>

          {showRecent && (
            <section className="activity-picker-recent">
              <div className="activity-picker-section-title"><Clock3 size={14} /><span>Récentes</span></div>
              <div className="activity-picker-list compact">
                {recent.map((activity) => {
                  const usageStatus = statuses.get(activity.id) ?? "not-evaluated";
                  return <button type="button" key={`recent-${activity.id}`} onClick={() => choose(activity.id)}><strong>{formatChemistryText(activity.title)}</strong><span>{activity.chapter}</span><em className={`picker-status status-${usageStatus}`}>{statusLabel[usageStatus]}</em></button>;
                })}
              </div>
            </section>
          )}

          <section className="activity-picker-results">
            <div className="activity-picker-section-title"><span>{filtered.length} activité{filtered.length > 1 ? "s" : ""}</span></div>
            <div className="activity-picker-list">
              {filtered.map((activity) => {
                const usageStatus = statuses.get(activity.id) ?? "not-evaluated";
                return (
                  <button type="button" key={activity.id} className={activity.id === selectedId ? "selected" : ""} onClick={() => choose(activity.id)}>
                    <strong>{formatChemistryText(activity.title)}</strong>
                    <span>{themeFor(activity) || "Sans thème"} · {activity.chapter}</span>
                    <em className={`picker-status status-${usageStatus}`}>{statusLabel[usageStatus]}</em>
                  </button>
                );
              })}
              {filtered.length === 0 && <div className="activity-picker-empty">Aucune activité ne correspond à ces filtres.</div>}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
